import { callMutation } from '@darice/graphql-client';

const SUBMIT_ENROLLMENT_MUTATION = `
mutation SubmitEnrollment($input: SubmitEnrollmentInput!) {
  submitEnrollment(input: $input) {
    success
    submissionId
    message
    error
  }
}
`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export interface SubmitEnrollmentInputDto {
  name: string;
  email: string;
  companyName?: string;
  phone?: string;
  offerRouteKey?: string;
  message?: string;
  sourceUrl?: string;
}

export interface SubmitEnrollmentResultDto {
  success: true;
  submissionId: string;
  message: string;
}

export async function submitEnrollment(input: SubmitEnrollmentInputDto): Promise<SubmitEnrollmentResultDto> {
  const dto = parseSubmitEnrollmentInputDto(input);
  const result = await callMutation<unknown>(SUBMIT_ENROLLMENT_MUTATION, { input: dto }, { useApiKey: true });
  return parseSubmitEnrollmentResultDto(result);
}

export function parseSubmitEnrollmentInputDto(raw: unknown): SubmitEnrollmentInputDto {
  if (!isRecord(raw)) throw new Error('FAIL: SubmitEnrollmentInputDto must be an object');
  const forbiddenTenantField = ['tenant', 'Id'].join('');
  if (forbiddenTenantField in raw) throw new Error('FAIL: submitEnrollment does not accept tenant override');
  const dto: SubmitEnrollmentInputDto = {
    name: requiredString(raw, 'name'),
    email: requiredString(raw, 'email').toLowerCase(),
  };
  if (!EMAIL_RE.test(dto.email)) throw new Error('FAIL: email must be an email address');
  for (const field of ['companyName', 'phone', 'offerRouteKey', 'message', 'sourceUrl'] as const) {
    const value = optionalString(raw, field);
    if (value) dto[field] = value;
  }
  if (dto.sourceUrl && !/^https:\/\/signup\.daricestrategies\.com\/.*$/u.test(dto.sourceUrl)) {
    throw new Error('FAIL: sourceUrl must be a signup.daricestrategies.com URL');
  }
  return dto;
}

export function parseSubmitEnrollmentResultDto(raw: unknown): SubmitEnrollmentResultDto {
  if (!isRecord(raw)) throw new Error('FAIL: SubmitEnrollmentResultDto must be an object');
  if (raw.success !== true) throw new Error('FAIL: submitEnrollment result must be successful');
  const submissionId = requiredString(raw, 'submissionId');
  if (!/^ENROLLMENT#[0-9a-f-]{36}$/u.test(submissionId)) {
    throw new Error('FAIL: submitEnrollment result submissionId must be an ENROLLMENT id');
  }
  return {
    success: true,
    submissionId,
    message: requiredString(raw, 'message'),
  };
}

function requiredString(input: Record<string, unknown>, field: string): string {
  const value = input[field];
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`FAIL: ${field} must be a non-empty string`);
  }
  return value.trim();
}

function optionalString(input: Record<string, unknown>, field: string): string | undefined {
  const value = input[field];
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new Error(`FAIL: ${field} must be a non-empty string when provided`);
  }
  return value.trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
